/**
 * Property Page Service
 * Automatically creates pages for new property listings using the agent's existing website template
 */

import { pageAPI } from './api.js';

/**
 * Extract the current website template from GrapesJS editor
 * Gets CSS styles and HTML structure to reuse for property pages
 * 
 * @param {Object} editor - GrapesJS editor instance
 * @returns {Object} Template object with html and css
 */
export function extractWebsiteTemplate(editor) {
  if (!editor) {
    console.error('❌ Editor not available for template extraction');
    return null;
  }

  try {
    // Get current CSS styles
    const css = editor.getCss();
    
    // Get current HTML structure
    const html = editor.getHtml();
    
    // Get wrapper component to identify structure
    const wrapper = editor.getWrapper();
    
    console.log('✅ Website template extracted:', {
      cssLength: css.length,
      htmlLength: html.length,
      hasWrapper: !!wrapper
    });

    return {
      html,
      css,
      extractedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ Error extracting website template:', error);
    return null;
  }
}

/**
 * Generate property-specific HTML content
 * Creates HTML for property details using data-gjs-type references to real estate blocks
 * 
 * @param {Object} property - Property data object
 * @returns {string} HTML string for property content
 */
export function generatePropertyContent(property) {
  if (!property) {
    console.error('❌ Property data required');
    return '';
  }

  // Format price
  const formattedPrice = typeof property.price === 'number' 
    ? `$${property.price.toLocaleString()}` 
    : property.price;

  // Format address
  const fullAddress = property.location 
    ? `${property.location.address}, ${property.location.city}, ${property.location.state} ${property.location.zipCode}`
    : property.address || 'Address not available';

  // Format sqft
  const formattedSqft = typeof property.sqft === 'number'
    ? property.sqft.toLocaleString()
    : property.sqft || 'N/A';

  // Generate property content HTML using data-gjs-type references
  // This allows GrapesJS to automatically instantiate the registered component types
  const propertyContentHTML = `
    <!-- Property Featured Section -->
    <div data-gjs-type="property-featured" 
         data-property-id="${property._id || property.id}"
         data-property-title="${escapeHtml(property.title)}"
         data-property-price="${escapeHtml(formattedPrice)}"
         data-property-address="${escapeHtml(fullAddress)}"
         data-property-bedrooms="${property.bedrooms || 0}"
         data-property-bathrooms="${property.bathrooms || 0}"
         data-property-sqft="${formattedSqft}"
         data-property-description="${escapeHtml(property.description || '')}"
         data-property-images="${property.images ? property.images.join(',') : ''}">
    </div>

    <!-- Property Details Section -->
    <section style="padding: 60px 0; background: #f5f5f7;">
      <div style="max-width: 1200px; margin: 0 auto; padding: 0 20px;">
        <div style="background: white; border-radius: 16px; padding: 40px; box-shadow: 0 4px 20px rgba(0,0,0,0.08);">
          <h2 style="font-size: 2rem; font-weight: 700; margin: 0 0 30px 0; color: #1d1d1f;">Property Details</h2>
          
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 30px; margin-bottom: 40px;">
            <div>
              <div style="color: #86868b; font-size: 0.9rem; margin-bottom: 8px;">Bedrooms</div>
              <div style="font-size: 1.5rem; font-weight: 600; color: #1d1d1f;">${property.bedrooms || 'N/A'}</div>
            </div>
            <div>
              <div style="color: #86868b; font-size: 0.9rem; margin-bottom: 8px;">Bathrooms</div>
              <div style="font-size: 1.5rem; font-weight: 600; color: #1d1d1f;">${property.bathrooms || 'N/A'}</div>
            </div>
            <div>
              <div style="color: #86868b; font-size: 0.9rem; margin-bottom: 8px;">Square Feet</div>
              <div style="font-size: 1.5rem; font-weight: 600; color: #1d1d1f;">${formattedSqft} sqft</div>
            </div>
            ${property.lotSize ? `
            <div>
              <div style="color: #86868b; font-size: 0.9rem; margin-bottom: 8px;">Lot Size</div>
              <div style="font-size: 1.5rem; font-weight: 600; color: #1d1d1f;">${property.lotSize}</div>
            </div>
            ` : ''}
            ${property.yearBuilt ? `
            <div>
              <div style="color: #86868b; font-size: 0.9rem; margin-bottom: 8px;">Year Built</div>
              <div style="font-size: 1.5rem; font-weight: 600; color: #1d1d1f;">${property.yearBuilt}</div>
            </div>
            ` : ''}
            ${property.garage ? `
            <div>
              <div style="color: #86868b; font-size: 0.9rem; margin-bottom: 8px;">Garage</div>
              <div style="font-size: 1.5rem; font-weight: 600; color: #1d1d1f;">${property.garage} spaces</div>
            </div>
            ` : ''}
          </div>

          ${property.description ? `
          <div style="margin-bottom: 40px;">
            <h3 style="font-size: 1.3rem; font-weight: 600; margin: 0 0 15px 0; color: #1d1d1f;">Description</h3>
            <p style="font-size: 1rem; line-height: 1.6; color: #424245; margin: 0;">${escapeHtml(property.description)}</p>
          </div>
          ` : ''}

          ${property.features && property.features.length > 0 ? `
          <div style="margin-bottom: 40px;">
            <h3 style="font-size: 1.3rem; font-weight: 600; margin: 0 0 15px 0; color: #1d1d1f;">Features</h3>
            <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px;">
              ${property.features.map(feature => `
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="color: #007AFF;">✓</span>
                  <span style="color: #424245;">${escapeHtml(feature)}</span>
                </div>
              `).join('')}
            </div>
          </div>
          ` : ''}

          ${property.amenities && property.amenities.length > 0 ? `
          <div>
            <h3 style="font-size: 1.3rem; font-weight: 600; margin: 0 0 15px 0; color: #1d1d1f;">Amenities</h3>
            <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px;">
              ${property.amenities.map(amenity => `
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="color: #007AFF;">✓</span>
                  <span style="color: #424245;">${escapeHtml(amenity)}</span>
                </div>
              `).join('')}
            </div>
          </div>
          ` : ''}
        </div>
      </div>
    </section>

    <!-- Property Map Section (if coordinates available) -->
    ${property.location && property.location.coordinates ? `
    <div data-gjs-type="mapbox-with-pins" 
         data-center-lng="${property.location.coordinates[0]}"
         data-center-lat="${property.location.coordinates[1]}"
         data-property-id="${property._id || property.id}"
         style="height: 500px; margin: 60px 0;">
    </div>
    ` : ''}

    <!-- Contact Agent Form -->
    <div data-gjs-type="real-estate-contact-agent-form" 
         data-property-id="${property._id || property.id}"
         style="margin: 60px 0;">
    </div>
  `;

  return propertyContentHTML;
}

/**
 * Combine agent's template with property content
 * Inserts property content into the main content area of the agent's template
 * 
 * @param {Object} template - Agent's website template (html, css)
 * @param {string} propertyContent - Property-specific HTML content
 * @returns {Object} Combined page data (html, css)
 */
export function combineTemplateWithProperty(template, propertyContent) {
  if (!template || !template.html) {
    console.error('❌ Template required');
    return null;
  }

  if (!propertyContent) {
    console.error('❌ Property content required');
    return null;
  }

  try {
    // The agent's HTML already contains header, footer, navigation
    // We need to inject property content into the main content area
    
    // Strategy: Find the main content wrapper or body, and inject property content
    // For now, we'll append property content to the existing HTML
    // The agent's structure will be preserved
    
    // Simple approach: Append property content to existing HTML
    // In a more sophisticated implementation, you could parse the HTML
    // and find a specific content area (like <main> or a specific class)
    const combinedHTML = template.html + propertyContent;
    
    // CSS stays the same - agent's CSS applies to everything
    const combinedCSS = template.css || '';

    console.log('✅ Template and property content combined');

    return {
      html: combinedHTML,
      css: combinedCSS
    };
  } catch (error) {
    console.error('❌ Error combining template with property:', error);
    return null;
  }
}

/**
 * Create a page for a property listing
 * Extracts agent's template, generates property content, combines them, and creates the page
 * 
 * @param {Object} property - Property data object
 * @param {Object} editor - GrapesJS editor instance (optional, will use window.editor if not provided)
 * @returns {Promise<Object>} Created page object
 */
export async function createPropertyPage(property, editor = null) {
  try {
    // Get editor instance
    const editorInstance = editor || (typeof window !== 'undefined' ? window.editor : null);
    
    if (!editorInstance) {
      throw new Error('GrapesJS editor not available');
    }

    if (!property || !property._id) {
      throw new Error('Property data required with _id');
    }

    console.log('🏠 Creating page for property:', property._id);

    // Step 1: Extract agent's website template
    const template = extractWebsiteTemplate(editorInstance);
    if (!template) {
      throw new Error('Failed to extract website template');
    }

    // Step 2: Generate property-specific content
    const propertyContent = generatePropertyContent(property);
    if (!propertyContent) {
      throw new Error('Failed to generate property content');
    }

    // Step 3: Combine template with property content
    const combinedPage = combineTemplateWithProperty(template, propertyContent);
    if (!combinedPage) {
      throw new Error('Failed to combine template with property content');
    }

    // Step 4: Generate page slug from property title
    const pageSlug = property.pageSlug || generateSlug(property.title || `property-${property._id}`);

    // Step 5: Create page via API
    const pageData = {
      name: property.title || `Property ${property._id}`,
      slug: pageSlug,
      title: `${property.title} - Property Listing`,
      metaDescription: property.description 
        ? `${property.description.substring(0, 155)}...` 
        : `View details for ${property.title}`,
      metaKeywords: [
        property.title,
        property.location?.city,
        property.location?.state,
        property.propertyType,
        'real estate',
        'property listing'
      ].filter(Boolean),
      status: 'draft', // Start as draft, agent can publish later
      template: 'property-listing',
      html: combinedPage.html,
      css: combinedPage.css,
      components: {
        html: combinedPage.html
      },
      styles: {
        css: combinedPage.css
      },
      isHomePage: false
    };

    console.log('📄 Creating page with data:', {
      name: pageData.name,
      slug: pageData.slug,
      htmlLength: pageData.html.length,
      cssLength: pageData.css.length
    });

    const response = await pageAPI.createPage(pageData);
    
    // Handle different response formats
    // Backend may return { success: true, data: {...} } or directly return the page object
    if (response && !response.success && !response._id && !response.data) {
      throw new Error(response.error || 'Failed to create page');
    }

    const createdPage = response.data || response;
    const pageId = createdPage._id || createdPage.id;

    console.log('✅ Property page created successfully:', pageId);

    // Step 6: Return page info for linking to property
    return {
      pageId,
      pageSlug: createdPage.slug || pageSlug,
      pageUrl: `/${pageSlug}`,
      success: true
    };

  } catch (error) {
    console.error('❌ Error creating property page:', error);
    throw error;
  }
}

/**
 * Generate URL-friendly slug from text
 * 
 * @param {string} text - Text to convert to slug
 * @returns {string} URL-friendly slug
 */
function generateSlug(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Escape HTML to prevent XSS
 * 
 * @param {string} text - Text to escape
 * @returns {string} Escaped HTML
 */
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Property event handler
 * Processes property:created events and creates pages if editor is available
 * 
 * @param {Object} property - Property data
 * @param {Object} editor - GrapesJS editor instance (optional)
 * @param {Function} onPageCreated - Callback when page is created (optional)
 */
async function handlePropertyCreated(property, editor = null, onPageCreated = null) {
  if (!property) {
    console.warn('⚠️ Property event received but no property data');
    return;
  }

  console.log('📢 Property created event received:', property._id || property.id);

  try {
    // Check if page generation is enabled for this property
    if (property.pageGenerationStatus === 'disabled') {
      console.log('⏭️ Page generation disabled for this property');
      return;
    }

    // Check if page already exists
    if (property.associatedPageId) {
      console.log('⏭️ Property already has associated page:', property.associatedPageId);
      return;
    }

    // Check if editor is available
    const editorInstance = editor || (typeof window !== 'undefined' ? window.editor : null);
    
    if (!editorInstance) {
      console.log('⏭️ Editor not available - backend job will create page');
      // Backend job will handle page creation when editor is not open
      // UI will poll/listen for updates to show new page links
      return;
    }

    // Extract current template from GrapesJS
    const html = editorInstance.getHtml();
    const css = editorInstance.getCss();
    
    if (!html || !css) {
      console.warn('⚠️ Could not extract template from editor');
      return;
    }

    // Create the page
    const pageResult = await createPropertyPage(property, editorInstance);
    
    if (pageResult && pageResult.success) {
      console.log('✅ Property page created:', pageResult.pageId);
      
      // Update property with page link via API
      try {
        const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000';
        const token = localStorage.getItem('jwt') || localStorage.getItem('token');
        
        const updateResponse = await fetch(`${API_BASE}/api/properties/${property._id || property.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            associatedPageId: pageResult.pageId,
            pageSlug: pageResult.pageSlug,
            pageGenerationStatus: 'created'
          })
        });

        if (!updateResponse.ok) {
          throw new Error(`PUT /api/properties/:id failed with ${updateResponse.status}`);
        }

        console.log('✅ Property updated with page link');
      } catch (updateError) {
        console.error('⚠️ Failed to update property with page link:', updateError);
        // Don't fail the whole process if update fails
      }
      
      // Call callback if provided
      if (onPageCreated) {
        onPageCreated(property, pageResult);
      }

      // Dispatch event for other parts of the app
      window.dispatchEvent(new CustomEvent('property:page:created', {
        detail: {
          property,
          page: pageResult
        }
      }));
    }
  } catch (error) {
    console.error('❌ Failed to create property page:', error);
    
    // Dispatch error event
    window.dispatchEvent(new CustomEvent('property:page:creation:failed', {
      detail: {
        property,
        error: error.message
      }
    }));
  }
}

/**
 * Initialize SSE connection for property events
 * Subscribes to property events via Server-Sent Events and re-dispatches as DOM events
 * 
 * @param {string} apiBase - API base URL (optional)
 * @returns {EventSource|null} SSE connection or null if not available
 */
export function initializePropertySSE(apiBase = null) {
  if (typeof window === 'undefined') {
    return null;
  }

  const API_BASE = apiBase || import.meta.env.VITE_API_URL || 'http://localhost:4000';
  const token = localStorage.getItem('jwt') || localStorage.getItem('token');
  
  if (!token) {
    console.warn('⚠️ No auth token - skipping SSE connection');
    return null;
  }

  try {
    // Create SSE connection with auth token in query params
    const eventSource = new EventSource(`${API_BASE}/api/properties/events?token=${encodeURIComponent(token)}`);

    console.log('🔌 Connecting to property events SSE...');

    let connected = false;

    eventSource.onopen = () => {
      connected = true;
      console.log('✅ Property events SSE connected');
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Handle different event types
        if (data.type === 'property:created') {
          // Re-dispatch as DOM event for GrapesJS listener
          window.dispatchEvent(new CustomEvent('property:created', {
            detail: { property: data.property }
          }));
        } else if (data.type === 'property:updated') {
          window.dispatchEvent(new CustomEvent('property:updated', {
            detail: { property: data.property }
          }));
        } else if (data.type === 'property:page:created') {
          window.dispatchEvent(new CustomEvent('property:page:created', {
            detail: { property: data.property, page: data.page }
          }));
        }
      } catch (error) {
        console.error('❌ Error parsing SSE message:', error);
      }
    };

    eventSource.addEventListener('property:created', (event) => {
      try {
        const data = JSON.parse(event.data);
        window.dispatchEvent(new CustomEvent('property:created', {
          detail: { property: data.property || data }
        }));
      } catch (error) {
        console.error('❌ Error handling property:created SSE event:', error);
      }
    });

    eventSource.onerror = (error) => {
      if (!connected) {
        // Never successfully opened — likely wrong URL, MIME type mismatch, or backend unavailable.
        // Close immediately so the browser doesn't keep reconnecting.
        console.warn('⚠️ Property events SSE failed before connecting (wrong URL or content-type). Closing to prevent reconnect loop.');
        eventSource.close();
        delete window.propertyEventSource;
        return;
      }
      console.error('❌ Property events SSE error:', error);
    };

    // Store reference for cleanup
    window.propertyEventSource = eventSource;
    
    return eventSource;
  } catch (error) {
    console.error('❌ Failed to initialize property SSE:', error);
    return null;
  }
}

/**
 * Close SSE connection
 */
export function closePropertySSE() {
  if (typeof window !== 'undefined' && window.propertyEventSource) {
    window.propertyEventSource.close();
    delete window.propertyEventSource;
    console.log('🔌 Property events SSE disconnected');
  }
}

/**
 * Initialize property page creation event listener
 * Listens for new property events and automatically creates pages
 * 
 * @param {Object} editor - GrapesJS editor instance (optional)
 * @param {Function} onPageCreated - Callback when page is created (optional)
 */
export function initializePropertyPageListener(editor = null, onPageCreated = null) {
  console.log('🎧 Initializing property page creation listener...');

  // Listen for custom events from backend/webhook/SSE
  if (typeof window !== 'undefined') {
    window.addEventListener('property:created', async (event) => {
      const property = event.detail?.property || event.detail;
      await handlePropertyCreated(property, editor, onPageCreated);
    });

    console.log('✅ Property page creation listener initialized');
  }
}

/**
 * Poll for property/page updates
 * Checks for new pages created by backend jobs when editor is not open
 * 
 * @param {string} organizationId - Organization ID to filter properties
 * @param {number} intervalMs - Polling interval in milliseconds (default: 30000 = 30 seconds)
 * @returns {Function} Cleanup function to stop polling
 */
export function startPropertyPagePolling(organizationId, intervalMs = 30000) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000';
  const token = localStorage.getItem('jwt') || localStorage.getItem('token');
  
  if (!token || !organizationId) {
    console.warn('⚠️ Missing token or organizationId - skipping property polling');
    return () => {};
  }

  let lastCheckTime = Date.now();
  let pollingInterval = null;

  const checkForUpdates = async () => {
    try {
      // Fetch properties that have been updated since last check
      const response = await fetch(
        `${API_BASE}/api/properties?organizationId=${organizationId}&updatedAfter=${lastCheckTime}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const properties = data.data || data || [];

      // Check for properties with newly created pages
      // Ensure properties is an array before calling forEach
      if (Array.isArray(properties)) {
        properties.forEach(property => {
        if (property.associatedPageId && property.pageGenerationStatus === 'created') {
          // Dispatch event for UI to show new page link
          window.dispatchEvent(new CustomEvent('property:page:available', {
            detail: {
              property,
              pageId: property.associatedPageId,
              pageSlug: property.pageSlug
            }
          }));
        }
      });
      }

      lastCheckTime = Date.now();
    } catch (error) {
      console.error('❌ Error polling for property updates:', error);
    }
  };

  // Start polling
  pollingInterval = setInterval(checkForUpdates, intervalMs);
  console.log(`🔄 Started property page polling (every ${intervalMs}ms)`);

  // Initial check
  checkForUpdates();

  // Return cleanup function
  return () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
      console.log('🛑 Stopped property page polling');
    }
  };
}

export default {
  extractWebsiteTemplate,
  generatePropertyContent,
  combineTemplateWithProperty,
  createPropertyPage,
  initializePropertyPageListener,
  initializePropertySSE,
  closePropertySSE,
  startPropertyPagePolling
};

